import React from 'react';
import { Badge, DataTable, RowActions, EmptyValue, ClampText, type DataTableColumn } from '@/components/ui';
import { DataSourceItem } from '../../../types/DataSource';
import { useClientSort } from '../../../utils/useClientSort';

export interface DataSourceColumn {
  key: keyof Pick<
    DataSourceItem,
    'code' | 'name' | 'type' | 'property' | 'label' | 'sortOrder' | 'isActive'
  >;
  label: string;
}

interface DataSourcesTableProps {
  items: DataSourceItem[];
  columns: DataSourceColumn[];
  onEdit: (item: DataSourceItem) => void;
  onDelete: (item: DataSourceItem) => void;
  canEdit: boolean;
  canDelete: boolean;
  loading?: boolean;
}

const SORT_VALUES: Record<string, (i: DataSourceItem) => string | number | null | undefined> = {
  code: (i) => i.code,
  name: (i) => i.name,
  type: (i) => i.type,
  property: (i) => i.property,
  label: (i) => i.label,
  sortOrder: (i) => i.sortOrder,
  isActive: (i) => (i.isActive ? 1 : 0),
};

/**
 * One cell.
 *
 * Not `value || '-'`: sortOrder is legitimately 0 and false is a real value for isActive, and
 * both would render as empty under a falsy check - the two columns most likely to be read
 * as "not set" when they are set.
 */
const renderCell = (item: DataSourceItem, key: DataSourceColumn['key']) => {
  if (key === 'isActive') {
    return item.isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Retired</Badge>
    );
  }
  const value = item[key];
  if (value === null || value === undefined || value === '') return <EmptyValue />;
  // Name (Text / Name) is the long column: a 2-line clamp.
  return key === 'name' ? <ClampText>{String(value)}</ClampText> : String(value);
};

// DataTable `fill`: the list sits in a white card that is the page's one scroll box, with a
// sticky header, the first column pinned left and Actions pinned right (W-H5, W-M8).
const DataSourcesTable: React.FC<DataSourcesTableProps> = ({
  items,
  columns,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  loading = false,
}) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(items, SORT_VALUES);

  const tableColumns: DataTableColumn<DataSourceItem>[] = [
    ...columns.map<DataTableColumn<DataSourceItem>>((column, index) => ({
      key: column.key,
      header: column.label,
      sortable: true,
      mobile: index === 0 ? 'title' : 'meta',
      // Name (Text / Name) is the long column: more room and a 2-line clamp.
      headerClassName: column.key === 'name' ? 'tw-min-w-[18rem]' : undefined,
      // Retired rows are dimmed; DataTable has no row class, so each cell carries it.
      cell: (item) =>
        item.isActive === false ? (
          <span className="tw-opacity-60">{renderCell(item, column.key)}</span>
        ) : (
          renderCell(item, column.key)
        ),
    })),
    {
      key: 'actions',
      header: 'Actions',
      hidden: !(canEdit || canDelete),
      width: '5rem',
      align: 'right',
      mobile: 'trailing',
      cell: (item) => (
        <RowActions
          actions={[
            { kind: 'edit', onClick: () => onEdit(item), hidden: !canEdit },
            { kind: 'delete', onClick: () => onDelete(item), hidden: !canDelete },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      fill
      striped
      columns={tableColumns}
      data={sorted}
      rowKey={(item) => item.id}
      loading={loading}
      emptyMessage={loading ? 'Loading...' : 'No records found'}
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
    />
  );
};

export default DataSourcesTable;
